import { useCallback, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { api, ApiError } from '@/lib/api'
import {
  getSdAdminEmailChoice,
  hasSdAdminValue,
  sdAdminFinalText,
} from '@/components/tickets/ticket-detail-page-helpers'
import type {
  TicketSdAdminApiAction,
  TicketSdAdminApiActionResponse,
} from '@/types/api'

type UseTicketSdAdminActionsParams = {
  ticketId: string | null
  refreshThreadSoft: () => Promise<void>
}

export function useTicketSdAdminActions({
  ticketId,
  refreshThreadSoft,
}: UseTicketSdAdminActionsParams) {
  const [busyAction, setBusyAction] = useState<TicketSdAdminApiAction | null>(null)
  const [result, setResult] = useState<TicketSdAdminApiActionResponse | null>(null)
  const [emailChoice, setEmailChoice] = useState<{
    action: TicketSdAdminApiAction
    emails: string[]
    sendToCustomer?: boolean
  } | null>(null)
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null)

  const closeEmailChoice = useCallback(() => {
    setEmailChoice(null)
    setSelectedEmail(null)
  }, [])

  const runAction = useCallback(
    async (action: TicketSdAdminApiAction, email?: string, sendToCustomer = false) => {
      if (!ticketId) return
      setBusyAction(action)
      try {
        const actionResult = await api.ticketSdAdminApiAction(ticketId, action, {
          ...(email ? { email } : {}),
          ...(sendToCustomer ? { send_to_customer: true } : {}),
        })
        setResult(actionResult)
        setEmailChoice(null)
        setSelectedEmail(null)
        if (actionResult.sent_message_id) {
          await refreshThreadSoft()
        }
        showNotification({
          title: actionResult.label,
          message: actionResult.sent_message_id
            ? 'Сообщение отправлено клиенту.'
            : sdAdminFinalText(actionResult),
          color: hasSdAdminValue(actionResult) ? 'green' : 'yellow',
        })
      } catch (requestError) {
        const emailOptions = requestError instanceof ApiError ? getSdAdminEmailChoice(requestError.detail) : null
        if (emailOptions) {
          setEmailChoice({ action, emails: emailOptions, sendToCustomer })
          setSelectedEmail(emailOptions[0] ?? null)
          return
        }
        showNotification({
          title: 'Admin API',
          message: requestError instanceof ApiError ? requestError.message : 'Не удалось выполнить действие',
          color: 'red',
        })
      } finally {
        setBusyAction(null)
      }
    },
    [refreshThreadSoft, ticketId],
  )

  return {
    busyAction,
    closeEmailChoice,
    emailChoice,
    result,
    runAction,
    selectedEmail,
    setSelectedEmail,
  }
}
